import axiosInstance from "../axiosInstance/axiosInstance";
import { getCurrentUser } from "./currentUser";

import HouseKeepingDecision from "../pages/PostCheckInRequest/HouseKeeping/HouseKeepingDecision/HouseKeepingDecision";
import FoodRequestDecision from "../pages/PostCheckInRequest/FoodRequest/FoodRequestDecision/FoodRequestDecision";
import RestaurantDiningDecision from "../pages/RestaurantDining/RestaurantDiningDecision";
import SpaDecision from "../pages/PostCheckInRequest/Spa/SpaDecision/SpaDecision";
import TaxiDecision from "../pages/PostCheckInRequest/Taxi/TaxiDecision/TaxiDecision";
import TableBookingDecision from "../pages/PostCheckInRequest/TableBooking/TableBookingDecision/TableBookingDecision";
import WakeUpCallDecision from "../pages/PostCheckInRequest/WakeUpCall/WakeUpCallDecision/WakeUpCallDecision";
import RoomChangeDecision from "../pages/PostCheckInRequest/RoomChange/RoomChangeDecision/RoomChangeDecision";
import ConferenceDecision from "../pages/PostCheckInRequest/ConferenceRoom/ConferenceRoomDecision/ConferenceDecision";
import CustomDecision from "../pages/PostCheckInRequest/CustomRequest/CustomDecision/CustomDecision";
import DecisionLateCheckout from "../pages/PostCheckInRequest/LateCheckout/DecisionLateCheckout/DecisionLateCheckout";
import WifiDecision from "../pages/PostCheckInRequest/Wifi/WifiDecision/WifiDecision";
import CheckOutDecision from "../pages/PostCheckInRequest/CheckOut/CheckOutDecision/CheckOutDecision";
import DecisionEarlyCheckIn from "../pages/EarlyCheckIn/DecisionEarlyCheckIn/DecisionEarlyCheckIn";
import UpdateWebCheckIn from "../pages/WebCheckIn/UpdateWebCheckIn/UpdateWebCheckIn";
import CheckInInviteDecision from "../pages/CheckInInviteDecision/CheckInInviteDecision";
import EditCabBooking from "../pages/CabBooking/EditCabBooking/EditCabBooking";
import CabDecesion from "../pages/CabBooking/CabDecesion/CabDecesion";

// One entry per request type from GET /api/v1/users/{userId}/requests.
//
// Each `load` mirrors the handleClick of the matching card in
// src/pages/Bookings/*Card/*.jsx, and returns the exact location.state object
// that card hands to its decision page. The fetch URLs are deliberately copied
// verbatim: they are not uniform across types.

const formatDateMonth = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
};

// The shape most post-checkin requests share: fetched off the hotel path,
// handed over as `Data` plus the ids.
const hotelScopedRequest = (kind, dataKey = "Data", extraState) => async (request) => {
    const response = await axiosInstance.get(`/api/v1/hotels/${request.hotel_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/post-checkin/${kind}?guest_request_id=${request.id}`);

    return {
        [dataKey]: response.data,
        bookingId: request.booking_id,
        bookedRoomId: request.booked_room_id,
        userId: request.user_id,
        status: request.status,
        hotelId: request.hotel_id,
        requestId: request.id,
        ...(extraState ? extraState(request) : {}),
    };
};

const requestTypeMap = {
    "HouseKeeping": {
        Component: HouseKeepingDecision,
        load: async (request) => {
            const [response, response1] = await Promise.all([
                axiosInstance.get(`/api/v1/users/${request.user_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/post-checkin/housekeeping-request?guest_request_id=${request.id}`),
                axiosInstance.get(`/api/v1/hotels/${request.hotel_id}/bookings/${request.booking_id}/booked-rooms`),
            ]);

            const data = response.data;
            const roomData = response1.data.data;
            const serviceRoomData = Array.isArray(roomData)
                ? roomData.find(room => room.id === request.booked_room_id)
                : null;

            return {
                data: data,
                bookedRoomId: request.booked_room_id,
                bookingId: request.booking_id,
                userId: request.user_id,
                requestId: request.id,
                roomId: serviceRoomData?.id,
                status: request.status,
                hotelId: request.hotel_id,
                bookedRooms: serviceRoomData,
                staffStatus: request.staff_status,
            };
        },
    },

    "In Room Dining": {
        Component: FoodRequestDecision,
        reviewType: "Dining",
        load: async (request) => {
            const response = await axiosInstance.get(`/api/v1/users/${request.user_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/post-checkin/food-request?guest_request_id=${request.id}`);

            return {
                foodData: response.data,
                bookingId: request.booking_id,
                bookedRoomId: request.booked_room_id,
                userId: request.user_id,
                status: request.status,
                staffStatus: request.staff_status,
            };
        },
    },

    "Restaurant Dining": {
        Component: RestaurantDiningDecision,
        reviewType: "RestaurantDining",
        load: async (request) => {
            const response = await axiosInstance.get(`/api/v1/users/${request.user_id}/hotels/${request.hotel_id}/restaurant-food-request?guest_request_id=${request.id}`);

            return {
                foodData: response.data,
                bookingId: request.booking_id,
                bookedRoomId: request.booked_room_id,
                userId: request.user_id,
                status: request.status,
                staffStatus: request.staff_status,
            };
        },
    },

    "Spa": {
        Component: SpaDecision,
        load: hotelScopedRequest("spa-request", "Data", (request) => ({
            staffStatus: request.staff_status,
        })),
    },

    "PostCheckin Cab Request": {
        Component: TaxiDecision,
        load: async (request) => {
            const response = await axiosInstance.get(`/api/v1/hotels/${request.hotel_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/pre-post-request/cab-request?guest_request_id=${request.id}`);

            return {
                cabRequestData: response.data,
                hotelId: request.hotel_id,
                bookingId: request.booking_id,
                requestId: request.id,
            };
        },
    },

    "PreCheckin Cab Request": {
        // The only type whose screen depends on status: the guest either has a
        // decision to make, or an existing booking to edit.
        pickComponent: (request) => request.status === "Pending_Guest" ? CabDecesion : EditCabBooking,
        load: async (request) => {
            const response = await axiosInstance.get(`/api/v1/hotels/${request.hotel_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/pre-post-request/cab-request?guest_request_id=${request.id}`);

            return {
                cabRequestData: response.data,
                hotelId: request.hotel_id,
                bookingId: request.booking_id,
            };
        },
    },

    "Restaurant Table": {
        Component: TableBookingDecision,
        load: hotelScopedRequest("restaurant-table-request", "tableData"),
    },

    "Wake Up Call": {
        Component: WakeUpCallDecision,
        load: hotelScopedRequest("wakeup-call-request"),
    },

    "PostCheckin Room Change Request": {
        Component: RoomChangeDecision,
        load: hotelScopedRequest("room-change-request"),
    },

    "Conference Room": {
        Component: ConferenceDecision,
        load: hotelScopedRequest("conference-room-request"),
    },

    "Custom Request": {
        Component: CustomDecision,
        load: hotelScopedRequest("custom-request"),
    },

    "Get Wifi": {
        Component: WifiDecision,
        load: hotelScopedRequest("wifi-request"),
    },

    "Checkout": {
        Component: CheckOutDecision,
        // Checking out is not a service the hotel delivers, so it is never rated
        rateable: false,
        load: hotelScopedRequest("checkout-request", "Data", (request) => ({
            people: request.booking_number_of_people,
            checkInDate: formatDateMonth(request.booking_checkin_date),
            checkOutDate: formatDateMonth(request.booking_checkout_date),
            roomNumber: request.room_number,
        })),
    },

    "PostCheckin Late Checkout Request": {
        Component: DecisionLateCheckout,
        load: async (request) => {
            const [response, response1] = await Promise.all([
                axiosInstance.get(`/api/v1/users/${request.user_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/post-checkin/late-checkout-request/${request.id}/`),
                axiosInstance.get(`/api/v1/hotels/${request.hotel_id}/bookings/${request.booking_id}/booked-rooms`),
            ]);

            const data = response.data;
            const roomData = response1.data.data;
            const room = Array.isArray(roomData)
                ? roomData.find(room => room.id === request.booked_room_id)
                : null;

            return {
                data: data,
                bookedRoomId: request.booked_room_id,
                bookingId: request.booking_id,
                userId: request.user_id,
                requestId: request.id,
                roomId: room?.room_number || "",
                status: request.status,
                hotelId: request.hotel_id,
                bookedRooms: roomData,
            };
        },
    },

    "PreCheckin Early Checkin Request": {
        Component: DecisionEarlyCheckIn,
        load: async (request) => {
            const [response, response1] = await Promise.all([
                axiosInstance.get(`/api/v1/users/${request.user_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/pre-checkin/early-checkin-request/${request.id}/`),
                axiosInstance.get(`/api/v1/hotels/${request.hotel_id}/bookings/${request.booking_id}/booked-rooms`),
            ]);

            const data = response.data;

            // Sorted by booked room id to match the Room 1 / Room 2 numbering
            // used when the request was raised.
            const roomData = Array.isArray(response1.data.data)
                ? [...response1.data.data].sort((a, b) => String(a.id).localeCompare(String(b.id)))
                : response1.data.data;

            const index = Array.isArray(roomData)
                ? roomData.findIndex(room => room.id === request.booked_room_id)
                : -1;

            return {
                data: data,
                bookedRoomId: request.booked_room_id,
                bookingId: request.booking_id,
                userId: request.user_id,
                requestId: request.id,
                roomId: index + 1,
                status: request.status,
                hotelId: request.hotel_id,
                bookedRooms: roomData,
            };
        },
    },

    "Web Checkin": {
        Component: UpdateWebCheckIn,
        load: async (request) => {
            const response = await axiosInstance.get(`/api/v1/users/${request.user_id}/bookings/${request.booking_id}/web-checkin`);
            const webCheckin = response.data;

            const tagData = {
                purpose: webCheckin?.purpose || [],
                travelling_with: webCheckin?.travelling_with || [],
                dietary: webCheckin?.dietary || [],
                interests: webCheckin?.interests || [],
                arrival: webCheckin?.arrival || "",
            };

            return {
                bookingId: webCheckin.booking_id,
                hotelId: request.hotel_id,
                userId: request.user_id,
                bookedRooms: webCheckin.rooms,
                guests: (webCheckin.rooms || []).map(room => room.guests),
                checkInDate: webCheckin.checkin_date,
                checkOutDate: webCheckin.checkout_date,
                tagData: tagData,
                timeArrival: webCheckin?.arrival_time || "",
                webCheckin,
            };
        },
    },

    "Web Checkin Invite": {
        Component: CheckInInviteDecision,
        load: async (request) => {
            // None of the three feeds the next, so they go out together.
            const [response1, response, currentUser] = await Promise.all([
                axiosInstance.get(`/api/v1/hotels/${request.hotel_id}/bookings/${request.booking_id}/booked-rooms/${request.booked_room_id}/guest-requests/web-checkin`),
                axiosInstance.get(`/api/v1/users/${request.user_id}/web-checkin?guest_request_id=${request.id}`),
                getCurrentUser(),
            ]);

            const webCheckInId = response1.data.id;
            const data = response.data;

            return {
                requestId: request.id,
                userId: request.user_id,
                checkInDate: data.checkin_date,
                checkOutDate: data.checkout_date,
                guests: data.number_of_guests,
                hotelName: data.hotel_name,
                hotelAddress: data.hotel_address,
                name: data.request_created_by,
                webCheckInId: webCheckInId,
                contact: currentUser.contact,
                guestDetails: data.guest_detail,
            };
        },
    },
};

export default requestTypeMap;
